import { ExpressPay } from "./express-pay";
import { DeliveryUpdate } from "./delivery-update";
import { IBasketTotals, IUpdatedBasketResult, IBasketItemViewModel } from "./interfaces/IBasketTotals";
import { ISelectListItem } from "./interfaces/ISelectListItem";
import { IUpdatedDeliveryResult } from "./interfaces/IUpdatedDeliveryResult";
import { PayPal, PayPalCaller } from "./paypal";

export class Basket {
    private currencyDropdownFunc: (e: any) => void;

    public static async InitAsync(): Promise<Basket> {
        let basket = new Basket();
        await basket.InitialiseAsync();
        return basket;
    }

    private deliveryUpdater: DeliveryUpdate;
    private readonly formSelector = "#basket-form";
    private readonly form = document.querySelector(this.formSelector);
    private expressPay: ExpressPay;
    private payPal: PayPal;

    public async InitialiseAsync(): Promise<void> {
        this.deliveryUpdater = DeliveryUpdate.Init(null, (updated) => this.orderTotalsUpdated(updated));
        this.payPal = PayPal.Init((data, action) => this.onPayWithPayPalClicked(data, action), PayPalCaller.BasketPage);
        this.expressPay = await ExpressPay.InitAsync((resolve, reject) => this.onExpressPayClicked(resolve, reject),this.formSelector);
        this.WireUpBillingCurrencySelection();
        this.WireUpDeliveryCountrySelection();
        this.wireUpVoucherToggling();
    }

    private onExpressPayClicked(resolve: () => void, reject: () => void): void {
        if (window.validationService.isValid(this.form)) {
            resolve();
        } else {
            reject();
        }
    }

    private onPayWithPayPalClicked(data: any, actions: any): Promise<any> {
        if (window.validationService.isValid(this.form)) {
            return actions.resolve();
        }
        return actions.reject();
    }

    private setSelectItem(selectId: string, optionValue: string): void {
        let select = <HTMLSelectElement>document.getElementById(selectId);

        if (select != null) {
            for (let i = 0; i < select.options.length; i++) {
                if (select.options[i].value === optionValue) {
                    select.selectedIndex = i;
                    select.dispatchEvent(new Event("change"));
                    break;
                }
            }
        }
    }

    private orderTotalsUpdated(updated: IUpdatedDeliveryResult): void {
        let basketTotals = updated.totals;
        this.updateTotalFields(basketTotals);
        this.setFieldValue("DeliveryServicesAsJson", updated.deliveryServicesAsJson);
        this.setFieldValue("DeliveryServices_IsStoreDelivery", this.deliveryUpdater.selectedDeliveryTypeIsStore().toString());

        // Reset Express Pay components so that the new totals are picked up.
        this.expressPay.resetExpressPay();
    }

    private updateTotalFields(basketTotals: IBasketTotals): void {
        this.setFieldValue("BasketTotals_CurrencyIsoCode", basketTotals.currencyIsoCode);
        this.setFieldValue("BasketTotals_TotalInUnits", basketTotals.totalInUnits.toString());
        this.setFieldValue("BasketTotals_SubTotalAsDecimal", basketTotals.subTotalAsDecimal);
        this.setFieldValue("BasketTotals_TotalAsDecimal", basketTotals.totalAsDecimal);
        this.setFieldValue("BasketTotals_DeliveryAsDecimal", basketTotals.deliveryAsDecimal);
    }

    private setFieldValue(fieldId: string, value: string): void {
        let element = (<HTMLInputElement>document.getElementById(fieldId));

        if (element) {
            element.value = value;
        }
    }

    private WireUpDeliveryCountrySelection(): void {
        let countryDropdown = document.getElementById("AddressCountryCode3");

        if (countryDropdown) {
            countryDropdown.addEventListener("change", (e) => this.onCountryChanged(e));
        }
    }

    private WireUpBillingCurrencySelection(): void {
        let currencyDropdown = document.getElementById("BillingCurrency");

        if (currencyDropdown) {
            // Save the function reference so we can remove it later.
            this.currencyDropdownFunc = (e) => this.onBillingCurrencyChanged(e);
            currencyDropdown.addEventListener("change", this.currencyDropdownFunc);
        }
    }

    private removeBillingCurrencyEvent(): void {
        let currencyDropdown = document.getElementById("BillingCurrency");

        if (currencyDropdown && this.currencyDropdownFunc) {
            currencyDropdown.removeEventListener("change", this.currencyDropdownFunc);
        }
    }

    private async onBillingCurrencyChanged(e: Event): Promise<void> {
        await this.RequestOrderUpdate(this.selectedDeliveryCountryCode(), this.selectedBillingCurrency());
    }

    private async onCountryChanged(e: Event): Promise<void> {
        await this.RequestOrderUpdate(this.selectedDeliveryCountryCode(), this.selectedBillingCurrency());
    }

    public async RequestOrderUpdate(countryCode3: string, billingCurrency: string) {
        let modalDivElement = <HTMLDivElement>document.getElementById("delivery-update-modal");
        if (modalDivElement) {
            window.Modal.OpenModal(modalDivElement, () => { });
        }

        let postData = {
            billingCurrency: billingCurrency,
            countryCode3: countryCode3,
        };

        const response = await fetch("/ws/update-basket", {
            body: JSON.stringify(postData),
            method: "post",
            headers: { 'content-type': 'application/json' }
        });

        const data: IUpdatedBasketResult = await response.json();

        window.Modal.CloseModal();
        await this.updateBasketUi(data);
    }

    private selectedDeliveryCountryCode(): string {
        let countryElement = <HTMLSelectElement>document.getElementById("AddressCountryCode3");

        if (!countryElement || countryElement.selectedIndex < 0) {
            return "";
        }

        return countryElement.options[countryElement.selectedIndex].value;
    }

    private selectedBillingCurrency(): string {
        let currencyElement = <HTMLSelectElement>document.getElementById("BillingCurrency");

        if (!currencyElement || currencyElement.selectedIndex < 0) {
            return "";
        }

        return currencyElement.options[currencyElement.selectedIndex].value;
    }

    private wireUpVoucherToggling(): void {
        const link = document.querySelector('.voucher-link a');

        if (link) {
            link.addEventListener("click", (e) => this.onVoucherClicked(e));
        }
    }

    private onVoucherClicked(e: Event): void {
        e.preventDefault();
        const foldableVoucher = document.querySelector(".foldable-voucher") as HTMLElement;

        if (foldableVoucher) {
            foldableVoucher.classList.toggle("hidden");
        }
    }

    private async updateBasketUi(updatedBasket: IUpdatedBasketResult) {
        this.updateCurrencyAndSalesTax(updatedBasket);
        this.setFieldValue("AddressPostalCode", updatedBasket.postalCode);
        this.deliveryUpdater.ReplaceDeliveryOptions(updatedBasket.deliveryServicesHtml);
        this.deliveryUpdater.UpdateOrderTotals(updatedBasket.totals);
        this.updateTotalFields(updatedBasket.totals);
        this.deliveryUpdater.UpdateUiForDeliveryType(); // Delivery type may have been changed by server (for example, store deselected if not in UK).

        this.repopulateOrderErrors(updatedBasket.orderMessages);
        this.setEUChargeVisibility(updatedBasket.isEUDelivery, updatedBasket.iossOutsideThreshold);
        this.overlayUpdatedPrices(updatedBasket);
        this.rebuildCurrencyOptions(updatedBasket.currencies);
        this.updateVoucherSavingText(updatedBasket.totals.voucherSavingText);

        // Reset any Adyen Express Pay components and PayPal so that any new currencies and totals are picked up.
        this.payPal.reloadPayPal(updatedBasket.totals.currencyIsoCode);
        this.expressPay.resetExpressPay();
    }

    private updateCurrencyAndSalesTax(updatedBasket: IUpdatedBasketResult): void {
        document.body.dataset.destinationSalesTaxRate = updatedBasket.salesTaxRate.toFixed(2);
        document.body.dataset.currency = updatedBasket.currencies.filter(c => c.selected)[0].value;
    }

    private updateVoucherSavingText(voucherSavingText: string): void {
        let amountSavingElement = document.getElementById("voucher-total-saving");

        if (amountSavingElement) {
            amountSavingElement.innerHTML = voucherSavingText;
        }
    }

    private setEUChargeVisibility(isEUDelivery: boolean, iossOutsideThreshold: boolean): void {
        let containers = document.getElementsByClassName("eu-delivery");

        if (containers) {
            for (const container of containers) {

                if (isEUDelivery) {
                    let outsideThresholdMessage = container.querySelector(".ioss-outside-threshold-message");
                    let withinThresholdMessage = container.querySelector(".ioss-within-threshold-message");

                    if (iossOutsideThreshold) {
                        outsideThresholdMessage.classList.remove("hidden");
                        withinThresholdMessage?.classList.add("hidden");
                    }
                    else {
                        outsideThresholdMessage.classList.add("hidden");
                        withinThresholdMessage?.classList.remove("hidden");
                    }

                    container.classList.remove("hidden");
                }
                else {
                    container.classList.add("hidden");
                }
            }
        }
    }

    private repopulateOrderErrors(orderMessages: string[]) {
        let errorContainer = document.getElementById("error-container");

        errorContainer.innerHTML = "";

        for (const element of orderMessages) {
            let errorDiv = document.createElement("div");

            errorDiv.className = "error";
            errorDiv.innerText = element;

            errorContainer.appendChild(errorDiv);
        }
    }

    private overlayUpdatedPrices(updatedBasket: IUpdatedBasketResult): void {

        for (const element of updatedBasket.items) {
            let item = element;

            let priceTdElement = <HTMLTableCellElement>document.getElementById(`item-price-${item.orderItemShardId}`);
            let subTotalTdElement = <HTMLTableCellElement>document.getElementById(`item-subtotal-${item.orderItemShardId}`);

            if (updatedBasket.voucher.isAbsolute) {
                priceTdElement.innerText = item.formattedPrice;
                subTotalTdElement.innerText = item.formattedPriceSubTotal;
            }
            else {
                subTotalTdElement.innerText = item.formattedEffectivePriceWithoutDiscountSubTotal;
                priceTdElement.innerHTML = "";

                priceTdElement.appendChild(document.createTextNode(item.formattedEffectivePriceWithoutDiscount));
                let savingElement = document.createElement("span");
                savingElement.className = "percentage-off";
                savingElement.innerText = item.formattedPercentageSaving;
                priceTdElement.appendChild(savingElement);
            }

            let itemMessagesTr = <HTMLTableRowElement>document.getElementById(`item-messages-${item.orderItemShardId}`);

            if (itemMessagesTr) {
                itemMessagesTr.innerHTML = "";
                for (const element of item.messages) {
                    let messageTd = document.createElement("td");
                    let messageDiv = document.createElement("div");

                    messageTd.colSpan = 5;
                    messageDiv.className = "message";
                    messageDiv.innerText = element;
                    messageTd.appendChild(messageDiv);
                    itemMessagesTr.appendChild(messageTd);
                }
            }

            this.updateRecommendedAccessory(item);
        }
    }

    private updateRecommendedAccessory(item: IBasketItemViewModel): void {
        let relatedTdElement = <HTMLTableCellElement>document.getElementById(`item-linked-${item.orderItemShardId}`);

        relatedTdElement.innerHTML = "";

        if (!item.linkedProduct) {
            return;
        }

        let containerDiv = document.createElement("div");
        containerDiv.className = "recommended-box";

        let h4Title = document.createElement("h4");
        h4Title.className = "title";
        h4Title.innerText = "Recommended accessory:";
        containerDiv.appendChild(h4Title);

        let imageAnchor = document.createElement("a");
        imageAnchor.className = "image";
        imageAnchor.href = item.linkedProduct.styleUrl;

        let productImage = document.createElement("img");
        productImage.alt = item.linkedProduct.description;
        productImage.height = 100;
        productImage.src = item.linkedProduct.imageUrl;
        productImage.width = 100;
        imageAnchor.appendChild(productImage);
        containerDiv.appendChild(imageAnchor);

        let infoDiv = document.createElement("div");
        infoDiv.className = "info-box";

        let nameAnchor = document.createElement("a");
        nameAnchor.className = "name";
        nameAnchor.href = item.linkedProduct.styleUrl;
        nameAnchor.innerText = item.linkedProduct.name;
        infoDiv.appendChild(nameAnchor);

        let specsList = document.createElement("ul");
        specsList.className = "info";
        let specsCode = document.createElement("li");
        specsCode.innerText = "Code: " + item.linkedProduct.styleCode;
        specsList.appendChild(specsCode);
        let specsSize = document.createElement("li");
        specsSize.innerText = "Size: " + item.linkedProduct.size;
        specsList.appendChild(specsSize);
        let specsColour = document.createElement("li");
        specsColour.className = "last";
        specsColour.innerText = "Colour: " + item.linkedProduct.colour;
        specsList.appendChild(specsColour);
        infoDiv.appendChild(specsList);

        let priceSpan = this.createPriceSpan("", "price")
        priceSpan.appendChild(this.createPriceSpan(item.linkedProduct.formattedPrice, "currprice"));
        infoDiv.appendChild(priceSpan);

        let addToBasket = document.createElement("input");
        addToBasket.className = "please-add";
        addToBasket.name = `add-${item.linkedProduct.productId}`;
        addToBasket.type = "submit";
        addToBasket.value = "Add to Basket";
        infoDiv.appendChild(addToBasket);

        containerDiv.appendChild(infoDiv);
        relatedTdElement.appendChild(containerDiv);
    }

    private createPriceSpan(price: string, className: string): HTMLSpanElement {
        let priceSpan = document.createElement("span");
        priceSpan.className = className;
        priceSpan.innerText = price;
        return priceSpan;
    }

    /**
     * Workaround to prevent non GBP currencies showing up in the list of
     * accepted currencies when GBR is not selected as the delivery address.
     * This code can be removed when the filing of VAT returns for EUR orders
     * to GBR has been resolved. Search for #GBP-HMRC-EUR.
     * @param currencies
     */
    private rebuildCurrencyOptions(currencies: ISelectListItem[]): void {
        let selectElement = <HTMLSelectElement>document.getElementById("BillingCurrency");

        this.removeBillingCurrencyEvent();

        selectElement.options.length = 0; // Quick clear of existing options.

        // Now rebuild with the new list of currencies.
        for (const currency of currencies) {
            let option = document.createElement("option");

            option.innerHTML = currency.text;
            option.selected = currency.selected;
            option.value = currency.value;

            selectElement.options.add(option);
        }

        this.WireUpBillingCurrencySelection();
    }
}

if (document.body.dataset.pageInit == "basket") {
    Basket.InitAsync();
}
